// System directives

'use client';

// System dependencies

import Image from 'next/image';
import Link from 'next/link';
import { useEffect } from 'react';

// Application dependencies

import Navlink from '@/components/nav/public/navLink/Navlink';
import { NAV_HEADER_NAME } from '@/interfaces/nav.interfaces';
import { RouteFields, RouteNames } from '@/interfaces/route.interfaces';
import { clearFormSpinner } from '@/lib/functions';

const HomePage = () => {
  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect to handle the form spinner control on component mount
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    clearFormSpinner();
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  return (
    <div className="dark:bg-slate-800 dark:text-zinc-300">
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
        {/* Jumbotron */}
        <section className="relative h-screen bg-white bg-cover bg-center dark:bg-zinc-700" style={{ backgroundImage: "url('/assets/core/core_portal_landing.png')" }}>
          <div className="absolute inset-0 bg-black opacity-30 dark:opacity-60"></div> {/* Optional overlay for better text visibility */}
          <div className="relative z-10 mx-auto grid h-full max-w-screen-xl px-4 py-8 lg:grid-cols-12 lg:gap-8 lg:py-16 xl:gap-0">
            <div className="place-self-center lg:col-span-7">
              <h1 className="max-w-2xl text-3xl font-semibold text-white/90 dark:text-white/60 md:text-4xl xl:text-5xl">
                welcome to auto<span className="text-white/60 dark:text-white/30">it</span>&apos;s
              </h1>
              <h1 className="max-w-2xl py-8 text-4xl font-bold text-white md:text-5xl xl:text-6xl">PORTAL ADMIN</h1>
              <p className="mb-6 max-w-2xl font-light text-white/90 dark:text-white/50 md:text-lg lg:mb-8 lg:text-xl">
                Providing a system and dealer administration template for auto
                <span className="text-white/60 dark:text-white/30">it&apos;s</span>
                <span className="font-semibold"> AIT Cloud </span>suite of products and services.
              </p>
            </div>
          </div>
        </section>
        {/* Technologies We Use */}
        <section className="bg-slate-300 dark:bg-slate-800">
          <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
            <h2 className="flex items-center justify-center text-3xl font-semibold dark:text-white">Technologies We Use</h2>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
              <div className="flex items-center justify-center">
                <Image alt="Next.js" className="h-[70px] w-[70px] dark:invert" src="/assets/nextjs-icon.svg" width={70} height={70} />
              </div>
              <div className="flex items-center justify-center">
                <Image alt="Typescript" className="h-[70px] w-[70px] dark:invert" src="/assets/typescript-icon.svg" width={70} height={70} />
              </div>
              <div className="flex items-center justify-center">
                <Image alt="Tailwind" className="h-[70px] w-[70px] dark:invert" src="/assets/tailwind-icon.svg" width={70} height={70} />
              </div>
              <div className="flex items-center justify-center">
                <Image alt="Redux" className="h-[70px] w-[70px] dark:invert" src="/assets/redux-icon.svg" width={70} height={70} />
              </div>
              <div className="flex items-center justify-center">
                <Image alt="SQLite" className="h-[70px] w-[70px] dark:invert" src="/assets/sqlite-icon.svg" width={70} height={70} />
              </div>
            </div>
            <p className="mb-6 mt-8 text-center font-light text-zinc-500 dark:text-zinc-400 md:text-lg lg:mb-8 lg:text-xl">
              A modern solutions using modern tools and built for all size applications. A full stack solution for the modern React programmer.
            </p>
          </div>
        </section>
        {/* Footer Section */}
        <footer className="bg-white p-4 dark:bg-slate-700 dark:text-zinc-300 sm:p-6">
          <div className="mx-auto max-w-screen-xl">
            <div className="md:flex md:justify-between">
              <div className="mb-6 md:mb-0">
                <Link className="flex space-x-3" href={RouteNames[RouteFields.Home].route}>
                  <Image alt="Logo" className="h-auto w-auto" height={28} src={'assets/logo-icon.svg'} width={28} />
                  <span className="self-center whitespace-nowrap text-base dark:text-white md:text-2xl">
                    <span className="text-logoblue dark:text-googleblue">{NAV_HEADER_NAME.Prefix}</span>
                    <span className="font-semibold">{NAV_HEADER_NAME.Suffix}</span>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 sm:gap-6">
                <div>
                  <h2 className="mb-6 text-sm font-semibold uppercase text-zinc-900 dark:text-white">Resources</h2>
                  <ul className="text-zinc-600 dark:text-zinc-400">
                    <li className="mb-4">
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Vercel" />
                    </li>
                    <li>
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Tailwind CSS" />
                    </li>
                  </ul>
                </div>
                <div>
                  <h2 className="mb-6 text-sm font-semibold uppercase text-zinc-900 dark:text-white">Follow us</h2>
                  <ul className="text-zinc-600 dark:text-zinc-400">
                    <li className="mb-4">
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Github" />{' '}
                    </li>
                    <li>
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Discord" />
                    </li>
                  </ul>
                </div>
                <div>
                  <h2 className="mb-6 text-sm font-semibold uppercase text-zinc-900 dark:text-white">Legal</h2>
                  <ul className="text-zinc-600 dark:text-zinc-400">
                    <li className="mb-4">
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Privacy Policy" />
                    </li>
                    <li>
                      <Navlink href={RouteNames[RouteFields.Home].route} label="Terms & Conditions" />
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <hr className="my-6 border-zinc-200 dark:border-zinc-700 sm:mx-auto lg:my-8" />
            <div className="sm:flex sm:items-center sm:justify-between">
              <span className="text-sm text-zinc-500 sm:text-center">
                © {new Date().getFullYear()} {' - '}
                <Link href={RouteNames[RouteFields.Home].route}>
                  <span className="text-black/60 dark:text-white/60">{NAV_HEADER_NAME.Prefix}</span>
                  <span className="font-semibold dark:text-zinc-200">{NAV_HEADER_NAME.Suffix}</span>
                </Link>
                {' - '} all rights reserved
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
